import {
  Content,
  FavoriteToggleIcon,
  PageWithHeader,
  TableColumn,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { isTechDocsAvailable } from '@backstage/plugin-techdocs';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import {
  getEntityLifecycle,
  getExplorerTableSx,
  renderLifecycleBadge,
} from '../entity-explorer/shared';
import { shellMetrics } from '../../theme/tokens';
import { ApiExplorerControls } from './explorer-page/ApiExplorerControls';
import { useApiExplorerState } from './explorer-page/useApiExplorerState';

const ApiExplorerTable = () => {
  const theme = useTheme();
  const configApi = useApi(configApiRef);
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();

  const openAppRoute = (path: string) => {
    const appBaseUrl = configApi.getOptionalString('app.baseUrl');
    if (!appBaseUrl) {
      window.location.assign(path);
      return;
    }

    const targetUrl = new URL(path.replace(/^\/+/, ''), `${appBaseUrl}/`);
    window.location.assign(targetUrl.toString());
  };

  const columns: TableColumn<CatalogTableRow>[] = [
    {
      ...CatalogTable.columns.createNameColumn({ defaultKind: 'API' }),
      width: '22%',
    },
    { ...CatalogTable.columns.createSystemColumn(), width: '16%' },
    { ...CatalogTable.columns.createOwnerColumn(), width: '14%' },
    {
      title: 'Lifecycle',
      field: 'entity.spec.lifecycle',
      width: '11%',
      customFilterAndSearch: (filter, { entity }) => {
        const lifecycle = getEntityLifecycle(entity);
        return (
          lifecycle?.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ??
          false
        );
      },
      render: ({ entity }) => renderLifecycleBadge(getEntityLifecycle(entity)),
    },
    {
      title: 'Description',
      field: 'entity.metadata.description',
      width: '25%',
      customFilterAndSearch: (filter, { entity }) =>
        entity.metadata.description
          ?.toLocaleLowerCase()
          .includes(filter.toLocaleLowerCase()) ?? false,
      render: ({ entity }) => entity.metadata.description ?? '—',
    },
  ];
  const actions = useMemo(
    () => [
      ({ entity }: CatalogTableRow) => {
        const namespace = entity.metadata.namespace ?? 'default';
        const kind = entity.kind.toLocaleLowerCase('en-US');
        const name = entity.metadata.name;
        const hasTechDocs = isTechDocsAvailable(entity);

        return {
          disabled: !hasTechDocs,
          icon: () => (
            <TableActionIcon
              disabled={!hasTechDocs}
              icon={<MenuBookOutlinedIcon fontSize="small" />}
              label="TechDocs"
            />
          ),
          onClick: () => openAppRoute(`/docs/${namespace}/${kind}/${name}`),
          tooltip: hasTechDocs ? 'Open TechDocs' : 'No TechDocs available',
        };
      },
      ({ entity }: CatalogTableRow) => {
        const namespace = entity.metadata.namespace ?? 'default';
        const kind = entity.kind.toLocaleLowerCase('en-US');
        const name = entity.metadata.name;
        const hasOpenApiDocs =
          String(entity.spec?.type ?? '').toLocaleLowerCase() === 'openapi';

        return {
          disabled: !hasOpenApiDocs,
          icon: () => (
            <TableActionIcon
              disabled={!hasOpenApiDocs}
              icon={<DescriptionOutlinedIcon fontSize="small" />}
              label="OpenAPI"
            />
          ),
          onClick: () =>
            openAppRoute(`/catalog/${namespace}/${kind}/${name}/definition`),
          tooltip: hasOpenApiDocs
            ? 'Open API definition'
            : 'No OpenAPI definition available',
        };
      },
      ({ entity }: CatalogTableRow) => {
        const namespace = entity.metadata.namespace ?? 'default';
        const kind = entity.kind.toLocaleLowerCase('en-US');
        const name = entity.metadata.name;

        return {
          icon: () => (
            <TableActionIcon
              icon={<OpenInNewOutlinedIcon fontSize="small" />}
              label="View"
            />
          ),
          onClick: () => openAppRoute(`/catalog/${namespace}/${kind}/${name}`),
          tooltip: 'Open entity',
        };
      },
      ({ entity }: CatalogTableRow) => {
        const isStarred = isStarredEntity(entity);

        return {
          icon: () => (
            <TableActionIcon
              active={isStarred}
              icon={<FavoriteToggleIcon isFavorite={isStarred} />}
              label="Star"
            />
          ),
          onClick: () => toggleStarredEntity(entity),
          tooltip: isStarred ? 'Remove star' : 'Star entity',
        };
      },
    ],
    [configApi, isStarredEntity, toggleStarredEntity],
  );

  return (
    <CatalogTable
      actions={actions}
      tableOptions={{
        actionsCellStyle: {
          color: theme.palette.text.secondary,
          textAlign: 'center',
        },
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        pageSize: 10,
        pageSizeOptions: [10, 20, 50],
        padding: 'dense',
        search: false,
        toolbar: false,
      }}
      columns={columns}
    />
  );
};

const TableActionIcon = ({
  active = false,
  disabled = false,
  icon,
  label,
}: {
  active?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        alignItems: 'center',
        color: disabled
          ? alpha(theme.palette.text.secondary, 0.24)
          : active
            ? theme.palette.warning.main
            : theme.palette.text.secondary,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        lineHeight: 1,
        minWidth: 52,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
          filter: disabled ? 'grayscale(1)' : 'none',
          height: 20,
          justifyContent: 'center',
          opacity: disabled ? 0.6 : 1,
          '& svg': {
            fontSize: 18,
          },
        }}
      >
        {icon}
      </Box>
      <Typography
        component="span"
        sx={{
          color: 'inherit',
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.01em',
          lineHeight: 1.1,
          opacity: disabled ? 0.72 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const ApiExplorerContent = () => {
  const theme = useTheme();
  const { searchText, setSearchText } = useApiExplorerState();

  return (
    <Stack spacing={2}>
      <ApiExplorerControls
        searchText={searchText}
        setSearchText={setSearchText}
      />

      <Box
        sx={{
          ...getExplorerTableSx(theme, { linkColumnIndices: [1, 2] }),
          '& td:last-child > div': {
            gap: theme.spacing(0.25),
            justifyContent: 'center',
          },
          '& td:last-child button': {
            borderRadius: `${theme.shape.borderRadius * 1.5}px`,
            padding: theme.spacing(0.75, 0.5),
          },
          '& th:last-child': { textAlign: 'center' },
        }}
      >
        <ApiExplorerTable />
      </Box>
    </Stack>
  );
};

export const CustomApiExplorerPage = () => {
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Backstage';

  return (
    <PageWithHeader
      subtitle={`${orgName} API Explorer`}
      title="APIs"
      themeId="apis"
    >
      <Content>
        <Box
          sx={{
            boxSizing: 'border-box',
            maxWidth: 'none',
            px: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Stack spacing={shellMetrics.sectionGap / 8}>
            <Alert severity="info">
              An Engineering VPN connection may be required to test API calls.
            </Alert>
            <EntityListProvider pagination={{ limit: 10, mode: 'offset' }}>
              <ApiExplorerContent />
            </EntityListProvider>
          </Stack>
        </Box>
      </Content>
    </PageWithHeader>
  );
};
